import { useState } from "react";
import { IconPlus, IconAlertTriangle } from "@tabler/icons-react";
import ModalShell from "../Support/ModalShell";
import Button from "../UI/Button/button";
import Input, { Select } from "../UI/FormInput/input";
import ColourPicker from "./ColourPicker";
import { getTeamApiError } from "./utils/apiError";

const DEFAULT_COLOUR = "#1d4ed8";
const EMPTY_FORM = { name: "", description: "", managerId: "" };

export default function AddTeamModal({ open, onClose, onAddTeam, users }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [colour, setColour] = useState(DEFAULT_COLOUR);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const reset = () => {
    setForm(EMPTY_FORM);
    setColour(DEFAULT_COLOUR);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Please enter a team name.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onAddTeam({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        colour,
        managerId: form.managerId || undefined,
      });
      reset();
    } catch (err) {
      setError(getTeamApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title="Create New Team"
      footer={
        <>
          <Button
            variant="outlineBlue"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<IconPlus size={15} />}
            onClick={handleSubmit}
            loading={submitting}
          >
            Create Team
          </Button>
        </>
      }
    >
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <IconAlertTriangle
            size={15}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <span>
            {typeof error === "string"
              ? error
              : "Please check the form and try again."}
          </span>
        </div>
      )}

      <Input
        label="Team Name"
        required
        id="newTeamName"
        placeholder="e.g. Engineering"
        value={form.name}
        onChange={setField("name")}
      />
      <Input
        label="Description (optional)"
        id="newTeamDesc"
        placeholder="What does this team do?"
        value={form.description}
        onChange={setField("description")}
      />
      <Select
        label="Team Manager"
        id="newTeamManager"
        value={form.managerId}
        onChange={setField("managerId")}
      >
        <option value="">Select manager…</option>
        {users?.map((u) => (
          <option key={u?._id} value={u?._id}>
            {u?.name}
          </option>
        ))}
      </Select>
      <div>
        <label
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--text-secondary)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            display: "block",
            marginBottom: 5,
          }}
        >
          Team Colour
        </label>
        <ColourPicker value={colour} onChange={setColour} />
      </div>
    </ModalShell>
  );
}
